import { useState } from "react";

import { css } from "@compiled/react";

import { token } from "@atlaskit/tokens";

import { wacToken } from "@/libraries/design/foundations";

export type ExperimentVariant = "control" | "treatment";

interface ExperimentOverrideControlsProps {
    experimentId: string;
    selectedVariant: ExperimentVariant | null;
    setSelectedVariant: (variant: ExperimentVariant | null) => void;
}

const PANEL_STYLES = css({
    position: "absolute",
    insetBlockStart: token("space.050"),
    insetInlineEnd: token("space.050"),
    zIndex: 2147483001,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "flex-end",
    columnGap: token("space.050"),
    color: token("color.text.inverse"),
    border: 0,
    boxShadow: token("elevation.shadow.overlay"),
    transformOrigin: "top right",
    borderRadius: "16px",
    maxWidth: "230px",
    padding: `${token("space.025")} ${token("space.050")}`,
    backgroundColor: wacToken("color.extended.blue900"),
    backdropFilter: "blur(4px)",
    opacity: 0.76,
    overflow: "hidden",
    transition:
        "max-width 220ms ease, padding 220ms ease, background-color 220ms ease, backdrop-filter 220ms ease, opacity 220ms ease, box-shadow 220ms ease"
});

const PANEL_EXPANDED_STYLES = css({
    zIndex: 2147483002,
    maxWidth: "328px",
    padding: token("space.075"),
    backgroundColor: wacToken("color.extended.blue900"),
    backdropFilter: "blur(12px)",
    opacity: 1
});

const META_STYLES = css({
    display: "grid",
    minWidth: 0,
    flex: 1,
    alignContent: "center"
});

const EXPERIMENT_ID_STYLES = css({
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontSize: "11px",
    lineHeight: 1.2,
    fontWeight: 700,
    minWidth: 0,
    maxWidth: "100%"
});

const BUTTON_CLUSTER_STYLES = css({
    display: "inline-flex",
    alignItems: "center",
    columnGap: token("space.025"),
    padding: token("space.025"),
    borderRadius: "999px",
    flexShrink: 0,
    transition: "background-color 180ms ease"
});

const BUTTON_CLUSTER_COLLAPSED_STYLES = css({
    backgroundColor: wacToken("color.neutral.dark800")
});

const BUTTON_CLUSTER_EXPANDED_STYLES = css({
    backgroundColor: wacToken("color.neutral.dark800")
});

const VARIANT_BUTTON_STYLES = css({
    "border": 0,
    "borderRadius": "999px",
    "minWidth": "58px",
    "padding": `${token("space.025")} ${token("space.075")}`,
    "fontSize": "10px",
    "lineHeight": 1.2,
    "cursor": "pointer",
    "fontWeight": 700,
    "transition":
        "background-color 180ms ease, color 180ms ease, transform 180ms ease",
    ":hover": {
        transform: "translateY(-1px)"
    },
    ":focus-visible": {
        outline: `2px solid ${token("color.border.focused")}`,
        outlineOffset: "2px"
    }
});

const SELECTED_VARIANT_BUTTON_STYLES = css({
    color: token("color.text"),
    backgroundColor: token("elevation.surface")
});

const UNSELECTED_VARIANT_BUTTON_STYLES = css({
    color: token("color.text.inverse"),
    backgroundColor: "transparent",
    opacity: 0.9
});

const getDisplayedExperimentLabel = (
    experimentId: string,
    isExpanded: boolean
): string => {
    if (isExpanded) {
        return experimentId;
    }

    return experimentId.length > 14
        ? `${experimentId.slice(0, 14)}…`
        : experimentId;
};

const getVariantButtonText = (variant: ExperimentVariant): string =>
    variant === "control" ? "Control" : "Treatment";

const getVariantButtonAriaLabel = (variant: ExperimentVariant): string =>
    variant === "control"
        ? "Show control (Original experience)"
        : "Show treatment (Experiment experience)";

const getVariantDescription = (variant: ExperimentVariant): string =>
    variant === "control" ? "Original experience" : "Experiment experience";

const VARIANTS: ExperimentVariant[] = ["control", "treatment"];

const renderVariantButton = (
    variant: ExperimentVariant,
    selectedVariant: ExperimentVariant | null,
    setSelectedVariant: (variant: ExperimentVariant | null) => void
) => {
    const isSelected = selectedVariant === variant;

    return (
        <button
            key={variant}
            type="button"
            css={[
                VARIANT_BUTTON_STYLES,
                isSelected
                    ? SELECTED_VARIANT_BUTTON_STYLES
                    : UNSELECTED_VARIANT_BUTTON_STYLES
            ]}
            aria-pressed={isSelected}
            aria-label={getVariantButtonAriaLabel(variant)}
            title={getVariantDescription(variant)}
            onClick={() => setSelectedVariant(isSelected ? null : variant)}
        >
            {getVariantButtonText(variant)}
        </button>
    );
};

const ExperimentOverrideControls = ({
    experimentId,
    selectedVariant,
    setSelectedVariant
}: ExperimentOverrideControlsProps) => {
    const [isExpanded, setIsExpanded] = useState(false);

    const handleActivate = () => {
        setIsExpanded(true);
    };

    const handleDeactivate = () => {
        setIsExpanded(false);
    };

    return (
        <div
            css={[PANEL_STYLES, isExpanded && PANEL_EXPANDED_STYLES]}
            data-testid="experiment-controls"
            onMouseEnter={handleActivate}
            onMouseLeave={handleDeactivate}
            onFocus={handleActivate}
            onBlur={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget)) {
                    handleDeactivate();
                }
            }}
        >
            <div css={META_STYLES}>
                <span
                    css={EXPERIMENT_ID_STYLES}
                    title={`Experiment ID: ${experimentId}`}
                    aria-label={`Experiment override for ${experimentId}`}
                >
                    {getDisplayedExperimentLabel(experimentId, isExpanded)}
                </span>
            </div>
            <div
                css={[
                    BUTTON_CLUSTER_STYLES,
                    isExpanded
                        ? BUTTON_CLUSTER_EXPANDED_STYLES
                        : BUTTON_CLUSTER_COLLAPSED_STYLES
                ]}
                role="group"
                aria-label="Experiment variant override"
            >
                {VARIANTS.map((variant) =>
                    renderVariantButton(
                        variant,
                        selectedVariant,
                        setSelectedVariant
                    )
                )}
            </div>
        </div>
    );
};

export default ExperimentOverrideControls;
